import React from "react";
import { bool, func } from "prop-types";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import Icon from "@components/shared/Icons/Icon";
import RequestButton from "@components/shared/RequestButton";
import Typography from "@components/shared/Typography";
import { hyphenate } from "@components/shared/utils";
import ResultItemLogo from "@components/TestDrive/FilterResultItems/ResultItemLogo";

import { testDriveLocationType } from "@types";
import { TEST_DRIVE_TYPE_ID } from "@shared/constants";

const convertToKilometers = (distanceInMeters, precision = 2) =>
  (distanceInMeters / 1000).toFixed(precision);

const FilterResultItem = ({
  isActive,
  onRequestError,
  onRequestSuccess,
  testDrivePoint,
  triggerRequestCallback,
}) => {
  const { t } = useTranslation();
  const isHomeTestDrive = testDrivePoint.type === TEST_DRIVE_TYPE_ID.HOME;

  return (
    <div
      className={twMerge(
        "rounded-md px-2 py-4 grid grid-template-mapSelectionItem gap-2 transition duration-300 border",
        isActive
          ? "bg-white border-greyLight"
          : "hover:bg-white/90 border-greyLight/50"
      )}
    >
      <div className="grid-area-icon flex items-start justify-center">
        <Icon
          className={twMerge(
            "w-6 h-auto",
            isActive ? "text-primaryOrange" : "text-primaryDark"
          )}
          icon={<ResultItemLogo testDrivePoint={testDrivePoint} />}
        />
      </div>
      <div className="grid-area-name">
        <Typography
          className={twMerge(
            "font-semibold",
            isActive ? "text-primaryOrange" : "text-primaryDark"
          )}
          data-id={`point-name-${hyphenate(testDrivePoint.name, /\.|\s/g)}`}
        >
          {testDrivePoint.name}
        </Typography>
      </div>
      <div className="grid-area-address" data-id="test-drive-point-address">
        <Typography className="text-sm">{testDrivePoint.address1}</Typography>
        <Typography className="text-sm">
          {testDrivePoint.zipcode} {testDrivePoint.city}
        </Typography>
        <Typography className="text-sm">
          {testDrivePoint.countryName}
        </Typography>
      </div>
      {(testDrivePoint.distanceInMeters || isHomeTestDrive) && (
        <div className="grid-area-distance flex items-center min-[32rem]:justify-end min-[32rem]:items-start">
          <Typography
            className="text-sm whitespace-nowrap"
            data-id={`delivery-point-distance-${testDrivePoint.distanceInMeters}`}
          >
            {convertToKilometers(testDrivePoint.distanceInMeters)}{" "}
            {t("general.kilometerSymbol")}
          </Typography>
        </div>
      )}

      <div className="grid-area-button">
        <div className="flex justify-end items-end size-full">
          <RequestButton
            className="text-xs"
            data-id="filtered-result-item-select-button"
            onRequestError={onRequestError}
            onRequestSuccess={onRequestSuccess}
            size="small"
            triggerRequestCallback={triggerRequestCallback}
            variant={isActive ? "primary" : "secondary"}
          >
            {isActive ? t("testDrive.selected") : t("testDrive.select")}
          </RequestButton>
        </div>
      </div>
    </div>
  );
};

FilterResultItem.propTypes = {
  isActive: bool,
  onRequestError: func.isRequired,
  onRequestSuccess: func.isRequired,
  testDrivePoint: testDriveLocationType,
  triggerRequestCallback: func.isRequired,
};

export default FilterResultItem;
